/*
 * Title: Reconcile Raw Data - ItemCard
 * Description: Displays a single reconciliation item with changes
 */

import { useState } from 'react';
import { Badge, Card, Col, Collapse, Row, Table } from 'react-bootstrap';

const typeConfig = {
    gain: { bg: 'success', label: 'GAIN', icon: 'fa-arrow-up' },
    loss: { bg: 'danger', label: 'LOSS', icon: 'fa-arrow-down' },
    equal: { bg: 'info', label: 'EQUAL', icon: 'fa-equals' },
    not_found_in_db: { bg: 'warning', label: 'NOT FOUND', icon: 'fa-question-circle' },
    db_only: { bg: 'dark', label: 'DB ONLY', icon: 'fa-database' },
};

const roundLabels = {
    exact: 'Exact Match',
    na_batch: 'N/A Batch Match',
    excel_only: 'Excel Only (New)',
    db_only: 'DB Only (Not in Excel)',
};

export default function ItemCard({ item }) {
    const [expanded, setExpanded] = useState(false);
    const config = typeConfig[item.type] || typeConfig.equal;

    return (
        <Card className={`mb-2 border-${config.bg}`} style={{ borderLeftWidth: '4px' }}>
            <Card.Header
                className="py-2 px-3 d-flex justify-content-between align-items-center"
                style={{ cursor: 'pointer', background: '#fafafa' }}
                onClick={() => setExpanded(!expanded)}
            >
                <div className="d-flex align-items-center gap-2">
                    <Badge bg={config.bg} className="me-1">
                        <i className={`fas ${config.icon} me-1`} />
                        {config.label}
                    </Badge>
                    <strong className="me-2">{item.pms_code}</strong>
                    <span className="text-muted me-2">{item.item_name}</span>
                    <small className="text-muted">
                        PO: {item.po_number} | Batch: {item.batch_number}
                    </small>
                </div>
                <div className="d-flex align-items-center gap-3">
                    {item.type === 'db_only' ? (
                        <span>
                            DB: <strong>{item.db_available?.toLocaleString()}</strong>
                        </span>
                    ) : (
                        <span>
                            Excel: <strong>{item.excel_available?.toLocaleString()}</strong> | DB:{' '}
                            <strong>{item.db_available?.toLocaleString()}</strong>{' '}
                            <Badge bg={item.diff > 0 ? 'success' : item.diff < 0 ? 'danger' : 'secondary'} className="ms-1">
                                {item.diff > 0 ? '+' : ''}
                                {item.diff?.toLocaleString()}
                            </Badge>
                        </span>
                    )}
                    <small className="text-muted">
                        <i className={`fas fa-chevron-${expanded ? 'up' : 'down'}`} />
                    </small>
                </div>
            </Card.Header>
            <Collapse in={expanded}>
                <div>
                    <Card.Body className="py-2 px-3" style={{ background: '#f9f9f9' }}>
                        <Row className="mb-2">
                            <Col lg={6}>
                                <small>
                                    <strong>Match Round:</strong> {roundLabels[item.match_round] || item.match_round}
                                </small>
                                {item.expiry_date && (
                                    <small className="ms-3">
                                        <strong>Expiry:</strong> {item.expiry_date}
                                    </small>
                                )}
                            </Col>
                            <Col lg={6} className="text-end">
                                {item.change_detail && (
                                    <small className={`fw-bold text-${config.bg === 'info' ? 'primary' : config.bg}`}>
                                        {item.change_detail}
                                    </small>
                                )}
                            </Col>
                        </Row>

                        {item.changes && item.changes.length > 0 && (
                            <Table size="sm" bordered className="mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th style={{ width: '10%' }}>#</th>
                                        <th style={{ width: '15%' }}>Action</th>
                                        <th style={{ width: '30%' }}>Table</th>
                                        <th>Description</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {item.changes.map((change) => (
                                        <tr key={`${change.action}-${change.table}`}>
                                            <td><i className="fas fa-check-circle text-success" /></td>
                                            <td>
                                                <Badge bg={change.action === 'INSERT' ? 'success' : change.action === 'UPDATE' ? 'warning' : 'info'}>
                                                    {change.action}
                                                </Badge>
                                            </td>
                                            <td>
                                                <code>{change.table}</code>
                                            </td>
                                            <td>
                                                <small>{change.description}</small>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}

                        {item.changes?.length === 0 && (
                            <small className="text-muted">No changes needed.</small>
                        )}
                    </Card.Body>
                </div>
            </Collapse>
        </Card>
    );
}
